import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { StatusBadge } from './dutiShared';
import { Sunrise, Loader2, Truck, TrendingUp, Clock, AlertTriangle, Activity, ArrowRight, Repeat, ShieldAlert } from 'lucide-react';

const ANOMALY_ICONS = { 'trending-up': TrendingUp, clock: Clock, 'alert-triangle': AlertTriangle, activity: Activity };
const ANOMALY_TONES = {
  critical: 'border-rose-200 bg-rose-50 text-rose-800',
  warning:  'border-amber-200 bg-amber-50 text-amber-800',
  info:     'border-sky-200 bg-sky-50 text-sky-800',
};
const GRADE_TONES = {
  'A+': 'bg-emerald-100 text-emerald-700', 'A': 'bg-emerald-100 text-emerald-700',
  'B': 'bg-sky-100 text-sky-700', 'C': 'bg-amber-100 text-amber-700',
  'D': 'bg-rose-100 text-rose-700', 'F': 'bg-rose-100 text-rose-700',
};

const GradePill = ({ grade, testId }) => grade ? (
  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${GRADE_TONES[grade] || 'bg-slate-100 text-slate-600'}`} data-testid={testId}>
    {grade}
  </span>
) : null;

const AnomalyPill = ({ a }) => {
  const Icon = ANOMALY_ICONS[a.icon] || Activity;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-xl border shadow-sm text-xs ${ANOMALY_TONES[a.severity] || ANOMALY_TONES.info}`}
      data-testid={`briefing-anomaly-${a.id}`}>
      <Icon className="h-3.5 w-3.5 shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="font-semibold leading-tight">{a.title}</p>
        <p className="opacity-75 leading-tight mt-0.5">{a.detail}</p>
      </div>
      {a.link && (
        <Button asChild variant="ghost" size="sm" className="h-6 px-2 text-[10px] ml-auto shrink-0">
          <Link to={a.link}>View <ArrowRight className="h-2.5 w-2.5 ml-0.5" /></Link>
        </Button>
      )}
    </div>
  );
};

const SupplierGroup = ({ g, canApprove, onSwitched }) => {
  const [switching, setSwitching] = useState(null);
  const canSwitch = canApprove && g.status === 'pending' && g.action_id && (g.alt_suppliers?.length > 0);
  // A switch can make two groups point at the same supplier_id — key/testid
  // on the always-unique action_id (falling back to supplier_id pre-draft).
  const gid = g.action_id || g.supplier_id;

  const handleSwitch = async (alt) => {
    setSwitching(alt.supplier_id);
    try {
      await api.post(`/duti/actions/${g.action_id}/switch-supplier`, { supplier_id: alt.supplier_id });
      toast.success(`Switched to ${alt.name} for this order`);
      onSwitched?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not switch supplier');
    } finally {
      setSwitching(null);
    }
  };

  return (
    <div className="rounded-lg border bg-background p-4 space-y-2" data-testid={`duti-briefing-group-${gid}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium flex items-center gap-2 min-w-0">
          <Truck className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="truncate">{g.supplier_name}</span>
          <GradePill grade={g.supplier_grade} testId={`duti-briefing-grade-${gid}`} />
        </p>
        {g.status ? <StatusBadge status={g.status} testId={`duti-briefing-status-${gid}`} />
          : <span className="text-xs text-muted-foreground shrink-0">No draft yet</span>}
      </div>
      {g.status === 'pending' && !canApprove && (
        <p className="text-xs text-muted-foreground" data-testid={`duti-briefing-awaiting-${gid}`}>Awaiting approval by purchasing staff</p>
      )}
      {g.via_category_fallback && (
        <p className="text-[11px] text-sky-700 bg-sky-50 border border-sky-200 rounded-md px-2 py-1" data-testid={`duti-briefing-category-fallback-${gid}`}>
          No direct order history for these items — supplier suggested from category history.
        </p>
      )}
      {g.quality_warning && (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 flex items-start gap-1.5" data-testid={`duti-briefing-quality-warning-${gid}`}>
          <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>{g.quality_warning}</span>
        </p>
      )}
      <ul className="text-sm space-y-1">
        {g.items.map((i) => (
          <li key={i.product_id} className="flex justify-between gap-2">
            <span className="truncate">{i.product_name}</span>
            <span className="text-muted-foreground shrink-0">{i.stock_on_hand}/{i.reorder_level} · order {i.quantity}</span>
          </li>
        ))}
      </ul>
      {canSwitch && (
        <div className="pt-1 border-t space-y-1" data-testid={`duti-briefing-alts-${gid}`}>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Alternate suppliers</p>
          <div className="flex flex-wrap gap-1.5">
            {g.alt_suppliers.map((alt) => (
              <Button key={alt.supplier_id} variant="outline" size="sm" className="h-6 px-2 text-[11px] gap-1"
                disabled={switching === alt.supplier_id} onClick={() => handleSwitch(alt)}
                data-testid={`duti-briefing-switch-${gid}-${alt.supplier_id}`}>
                {switching === alt.supplier_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Repeat className="h-3 w-3" />}
                {alt.name} <GradePill grade={alt.grade} />
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const RestockBriefing = ({ owner, onDrafted }) => {
  const { user } = useAuth();
  const canApprove = (user?.permissions || []).includes('purchases_manage');
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadBriefing = () => api.get('/duti/briefing').then(({ data }) => setB(data)).catch(() => setB({}));
  useEffect(() => { loadBriefing(); }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/duti/briefing/run');
      setB(data);
      if (data.drafts_created) {
        toast.success('Restock drafts sent to the approval queue');
      } else if (data.drafts_disabled_reason) {
        toast.warning(data.drafts_disabled_reason);
      }
      onDrafted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not prepare drafts');
    } finally {
      setBusy(false);
    }
  };

  if (!b) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
  const groups = b.groups || [];
  const total = groups.reduce((n, g) => n + g.items.length, 0) + (b.no_supplier?.length || 0);
  const anomalies = b.anomalies || [];

  return (
    <section className="rounded-2xl border bg-gradient-to-br from-amber-50 to-background p-6 space-y-5" data-testid="duti-restock-briefing">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <Sunrise className="h-6 w-6 text-amber-600 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold">Morning restock briefing</h2>
            <p className="text-sm text-muted-foreground" data-testid="duti-briefing-summary">
              {total ? `${total} item(s) at or below reorder level` : 'Nothing is running low today.'}
              {b.already_on_order?.length ? ` · ${b.already_on_order.length} already on order` : ''}
              {anomalies.length ? ` · ${anomalies.length} anomaly signal${anomalies.length > 1 ? 's' : ''}` : ''}
            </p>
          </div>
        </div>
        {owner && groups.length > 0 && !b.drafts_created && (
          <Button size="sm" onClick={runNow} disabled={busy} data-testid="duti-briefing-run-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Prepare drafts now
          </Button>
        )}
      </header>

      {/* Anomaly signals */}
      {anomalies.length > 0 && (
        <div className="space-y-2" data-testid="duti-briefing-anomalies">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Proactive signals</p>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {anomalies.map((a) => <AnomalyPill key={a.id} a={a} />)}
          </div>
        </div>
      )}

      {!b.drafts_created && b.drafts_disabled_reason && groups.length > 0 && (
        <p className="text-xs text-amber-800" data-testid="duti-briefing-disabled-reason">
          Drafts weren't made automatically: {b.drafts_disabled_reason}.
        </p>
      )}
      {groups.length > 0 && <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{groups.map((g) => <SupplierGroup key={g.action_id || g.supplier_id} g={g} canApprove={canApprove} onSwitched={loadBriefing} />)}</div>}
      {b.no_supplier?.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="duti-briefing-no-supplier">
          No previous supplier for: {b.no_supplier.map((i) => i.product_name).join(', ')} — order these manually.
        </p>
      )}
    </section>
  );
};
